import { useEffect, useId, useState } from 'react';

import { Badge, Button, Icon } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, SearchField, Select } from '@ds/forms';
import { Menu } from '@ds/navigation';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { CategoriaDeConta } from '@/data/tipos';

export interface CategoriasDeContaProps {
    categorias: CategoriaDeConta[];
    busca: string;
    onBusca: (valor: string) => void;
    onMudou: () => void;
}

export function CategoriasDeConta({ categorias, busca, onBusca, onMudou }: CategoriasDeContaProps) {
    const aviso = useAviso();
    const [editando, setEditando] = useState<CategoriaDeConta | null | undefined>(undefined);

    const alternar = async (categoria: CategoriaDeConta) => {
        try {
            await dados().inativarCategoriaDeConta(categoria.id, !categoria.ativa);
            aviso({
                title: categoria.ativa ? 'Categoria inativada' : 'Categoria reativada',
                description: categoria.ativa
                    ? `${categoria.nome} sai das listas de escolha; os lançamentos antigos continuam classificados`
                    : categoria.nome,
                tone: categoria.ativa ? 'warning' : 'success',
            });
            onMudou();
        } catch (e) {
            aviso({ title: 'Não foi possível mudar a categoria', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<CategoriaDeConta>[] = [
        { key: 'nome', header: 'Categoria', sortable: true, render: (c) => <strong style={{ fontWeight: 600 }}>{c.nome}</strong> },
        {
            key: 'natureza', header: 'Natureza',
            render: (c) => <Badge tone={c.natureza === 'receita' ? 'success' : 'warning'} dot>{c.natureza === 'receita' ? 'Receita' : 'Despesa'}</Badge>,
        },
        { key: 'grupo', header: 'Grupo', render: (c) => c.grupo ?? '—' },
        {
            key: 'lancamentos', header: 'Lançamentos', align: 'right', sortable: true,
            render: (c) => (c.lancamentos === 0 ? <span style={{ color: 'var(--text-tertiary)' }}>nenhum</span> : c.lancamentos),
        },
        {
            key: 'ativa', header: 'Situação',
            render: (c) => <Badge tone={c.ativa ? 'success' : 'neutral'} dot>{c.ativa ? 'Ativa' : 'Inativa'}</Badge>,
        },
        {
            key: 'acoes', header: '', align: 'right', width: 56,
            render: (c) => (
                <span data-parar-linha>
                    <Menu
                        align="right" width={220}
                        onSelect={(id) => {
                            if (id === 'editar') setEditando(c);
                            else if (id === 'situacao') void alternar(c);
                        }}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Ações de ${c.nome}`} />}
                        items={[
                            { id: 'editar', label: 'Editar categoria', icon: 'pencil' },
                            { id: 'divisor', label: '', divider: true },
                            {
                                id: 'situacao',
                                label: c.ativa ? 'Inativar categoria' : 'Reativar categoria',
                                icon: c.ativa ? 'ban' : 'rotate-ccw',
                                tone: c.ativa ? 'danger' : undefined,
                            },
                        ]}
                    />
                </span>
            ),
        },
    ];

    const visiveis = categorias.filter((c) => c.nome.toLowerCase().includes(busca.trim().toLowerCase()));

    return (
        <>
            <FilterBar
                search={<SearchField value={busca} onChange={onBusca} placeholder="Buscar categoria" width={240} />}
                resultLabel={`${visiveis.length} ${visiveis.length === 1 ? 'categoria' : 'categorias'}`}
                actions={<Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setEditando(null)}>Nova categoria</Button>}
            />

            <DataTable
                columns={colunas} rows={visiveis} rowKey="id"
                onRowClick={(c) => setEditando(c)}
                rowLabel={(c) => c.nome}
                emptyMessage="Nenhuma categoria com esse nome."
            />

            <CategoriaModal
                open={editando !== undefined}
                categoria={editando ?? null}
                candidatasAPai={categorias}
                onClose={() => setEditando(undefined)}
                onSalvo={onMudou}
            />
        </>
    );
}

interface CategoriaModalProps {
    open: boolean;
    categoria: CategoriaDeConta | null;
    candidatasAPai: CategoriaDeConta[];
    onClose: () => void;
    onSalvo: () => void;
}

function CategoriaModal({ open, categoria, candidatasAPai, onClose, onSalvo }: CategoriaModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idNatureza = useId();
    const idGrupo = useId();
    const [nome, setNome] = useState('');
    const [natureza, setNatureza] = useState<'receita' | 'despesa'>('despesa');
    const [paiId, setPaiId] = useState('');
    const formSujo = useAlteracaoPendente({ nome, natureza, paiId }, open && (categoria ?? 'nova'));
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setNome(categoria?.nome ?? '');
        setNatureza(categoria?.natureza ?? 'despesa');
        setPaiId(categoria?.paiId ?? '');
        setErro(null);
    }, [open, categoria]);

    /* Só categoria da MESMA natureza pode ser grupo, e nenhuma pode ser mãe de
       si mesma: "Estrutura > Estrutura" quebra o relatório em silêncio. */
    const grupos = candidatasAPai.filter((c) => c.natureza === natureza && c.id !== categoria?.id && !c.paiId);

    const salvar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            const salva = await dados().salvarCategoriaDeConta({
                id: categoria?.id, nome, natureza, paiId: paiId || undefined,
            });
            aviso({ title: categoria ? 'Categoria atualizada' : 'Categoria criada', description: salva.nome });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar a categoria.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={open} onClose={onClose} width={520}
            title={categoria ? `Editar ${categoria.nome}` : 'Nova categoria'}
            description="A categoria classifica o lançamento e é o eixo do relatório por categoria."
            dirty={formSujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando}>{categoria ? 'Salvar' : 'Criar categoria'}</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {categoria && categoria.lancamentos > 0 && (
                    <Alert tone="info" dismissible={false}>
                        {categoria.lancamentos === 1 ? 'Um lançamento usa' : `${categoria.lancamentos} lançamentos usam`} esta categoria.
                        Renomear muda o rótulo deles também — é a mesma classificação, com outro nome.
                    </Alert>
                )}

                <FormField label="Nome" required htmlFor={idNome}>
                    <Input id={idNome} value={nome} onChange={setNome} placeholder="Produtos e insumos" />
                </FormField>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <FormField label="Natureza" required htmlFor={idNatureza}>
                        <Select
                            id={idNatureza} value={natureza}
                            onChange={(v) => { setNatureza(v as 'receita' | 'despesa'); setPaiId(''); }}
                            options={[{ value: 'receita', label: 'Receita' }, { value: 'despesa', label: 'Despesa' }]}
                        />
                    </FormField>
                    <FormField label="Grupo" htmlFor={idGrupo} hint="Opcional — uma hierarquia rasa basta">
                        <Select
                            id={idGrupo} value={paiId} onChange={setPaiId}
                            options={[{ value: '', label: 'Sem grupo' }, ...grupos.map((c) => ({ value: c.id, label: c.nome }))]}
                        />
                    </FormField>
                </div>
            </div>
        </Modal>
    );
}
